/**
 * Company Management Component
 * CRUD operations for companies with data refresh functionality
 */
import { useState, useEffect } from 'react';
import api from '../utils/api';

// SVG Search Icon component
const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// SVG Folder Icon component
const FolderIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ed6c02"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [parentCompanies, setParentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    quickbooks_id: '',
    company_name: '',
    parent_company_id: '',
    new_parent_company_name: '',
    refresh_token: '',
  });
  const [showNewParentInput, setShowNewParentInput] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedParents, setCollapsedParents] = useState({});

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editData, setEditData] = useState({
    company_name: '',
    parent_company_id: '',
    refresh_token: ''
  });
  const [saving, setSaving] = useState(false);

  // Refresh data state
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingCompanyId, setRefreshingCompanyId] = useState(null);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);

  // Checkbox selections per company
  const [companySelections, setCompanySelections] = useState({});

  // Global selections for Refresh All
  const [globalSelections, setGlobalSelections] = useState({
    refresh_accounts: false,
    refresh_profit_loss: false,
    refresh_cashflow: false
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  // Initialize selections when companies load
  useEffect(() => {
    const initialSelections = {};
    companies.forEach(company => {
      if (!companySelections[company.company_id]) {
        initialSelections[company.company_id] = {
          refresh_accounts: false,
          refresh_profit_loss: false,
          refresh_cashflow: false
        };
      }
    });
    if (Object.keys(initialSelections).length > 0) {
      setCompanySelections(prev => ({ ...prev, ...initialSelections }));
    }
  }, [companies]);

  const loadCompanies = async () => {
    try {
      const [companiesRes, parentCompaniesRes] = await Promise.all([
        api.get('/companies'),
        api.get('/parent-companies'),
      ]);
      setCompanies(companiesRes.data);
      setParentCompanies(parentCompaniesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Create the company
      const companyPayload = {
        ...formData,
        parent_company_id: showNewParentInput ? undefined : formData.parent_company_id || null,
        new_parent_company_name: showNewParentInput ? formData.new_parent_company_name : undefined
      };

      const response = await api.post('/companies', companyPayload);
      const createdCompany = response.data;

      setShowCreateForm(false);
      setShowNewParentInput(false);
      setFormData({ quickbooks_id: '', company_name: '', parent_company_id: '', new_parent_company_name: '', refresh_token: '' });

      // Only auto-trigger data refresh if QuickBooks ID was provided
      if (createdCompany.quickbooks_id) {
        setRefreshingCompanyId(createdCompany.company_id);
        setRefreshing(true);
        setShowProgressModal(true);
        setRefreshProgress({
          type: 'single',
          company_name: createdCompany.company_name,
          steps: [{ step: 'starting', status: 'in_progress', message: 'Loading initial data from QuickBooks...' }],
          success: null,
          error: null,
          summary: null
        });

        try {
          // Refresh all data types by default
          const refreshResponse = await api.post(`/companies/${createdCompany.company_id}/refresh-data`, {
            refresh_accounts: true,
            refresh_profit_loss: true,
            refresh_cashflow: true
          });
          const refreshResult = refreshResponse.data;

          setRefreshProgress({
            type: 'single',
            company_name: createdCompany.company_name,
            steps: refreshResult.steps,
            success: refreshResult.success,
            error: refreshResult.error,
            summary: refreshResult.summary
          });
        } catch (refreshError) {
          setRefreshProgress({
            type: 'single',
            company_name: createdCompany.company_name,
            steps: [{ step: 'error', status: 'error', message: refreshError.response?.data?.detail || 'Failed to load initial data' }],
            success: false,
            error: refreshError.response?.data?.detail || 'Failed to load initial data',
            summary: null
          });
        } finally {
          setRefreshing(false);
          setRefreshingCompanyId(null);
        }
      }

      loadCompanies();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create company');
    }
  };

  // Open edit modal
  const handleEdit = (company) => {
    setEditingCompany(company);
    setEditData({
      company_name: company.company_name,
      parent_company_id: company.parent_company_id || '',
      refresh_token: ''
    });
    setShowEditModal(true);
  };

  // Save edit from modal
  const handleSaveEdit = async () => {
    if (!editingCompany) return;

    setSaving(true);
    try {
      const updateData = {
        company_name: editData.company_name,
        parent_company_id: editData.parent_company_id || null,
      };
      if (editData.refresh_token) {
        updateData.refresh_token = editData.refresh_token;
      }

      await api.put(`/companies/${editingCompany.company_id}`, updateData);
      setShowEditModal(false);
      setEditingCompany(null);
      setEditData({ company_name: '', parent_company_id: '', refresh_token: '' });
      loadCompanies();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  // Close edit modal
  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingCompany(null);
    setEditData({ company_name: '', parent_company_id: '', refresh_token: '' });
  };

  const handleDelete = async (companyId) => {
    if (!confirm(`Are you sure you want to delete company ${companyId}?`)) return;

    try {
      await api.delete(`/companies/${companyId}`);
      loadCompanies();
      alert('Company deleted successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  // Toggle company selection
  const toggleCompanySelection = (companyId, field) => {
    setCompanySelections(prev => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        [field]: !prev[companyId]?.[field]
      }
    }));
  };

  // Toggle global selection
  const toggleGlobalSelection = (field) => {
    setGlobalSelections(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Check if at least one data type is selected for a company
  const hasSelection = (companyId) => {
    const sel = companySelections[companyId];
    return sel && (sel.refresh_accounts || sel.refresh_profit_loss || sel.refresh_cashflow);
  };

  // Check if at least one global selection
  const hasGlobalSelection = () => {
    return globalSelections.refresh_accounts || globalSelections.refresh_profit_loss || globalSelections.refresh_cashflow;
  };

  // Toggle parent company collapse state
  const toggleParentCollapse = (parentId) => {
    setCollapsedParents(prev => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));
  };

  // Group companies by parent
  const getGroupedCompanies = () => {
    const grouped = {};
    const noParent = [];

    // Filter by search term
    const filteredCompanies = companies.filter(company =>
      company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(company.company_id).includes(searchTerm) ||
      (company.quickbooks_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.parent_company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    filteredCompanies.forEach(company => {
      if (company.parent_company_id) {
        if (!grouped[company.parent_company_id]) {
          grouped[company.parent_company_id] = {
            parent_company_id: company.parent_company_id,
            parent_company_name: company.parent_company_name || 'Unknown Parent',
            children: []
          };
        }
        grouped[company.parent_company_id].children.push(company);
      } else {
        noParent.push(company);
      }
    });

    return { grouped, noParent };
  };

  // Check if any child company has a selection
  const hasParentSelection = (parentId) => {
    const childCompanies = companies.filter(c => c.parent_company_id === parentId);
    return childCompanies.some(c => hasSelection(c.company_id));
  };

  // Refresh all child companies under a parent (always refreshes ALL data types)
  const handleRefreshParent = async (parentId, parentName) => {
    const childCompanies = companies.filter(c => c.parent_company_id === parentId);

    if (childCompanies.length === 0) {
      alert('No companies under this parent');
      return;
    }

    // Always refresh all data types for parent company refresh
    const refreshOptions = {
      refresh_accounts: true,
      refresh_profit_loss: true,
      refresh_cashflow: true
    };

    if (!confirm(`Refresh ALL QuickBooks data (Accounts, P&L, Cash Flow) for ${childCompanies.length} companies under "${parentName}"?\n\nThis may take a few minutes.`)) return;

    setRefreshing(true);
    setShowProgressModal(true);
    setRefreshProgress({
      type: 'all',
      steps: [{ step: 'starting', status: 'in_progress', message: `Processing ${childCompanies.length} companies under ${parentName}...` }],
      success: null,
      error: null,
      total_companies: childCompanies.length,
      successful: 0,
      failed: 0,
      companies: []
    });

    const results = [];
    let successful = 0;
    let failed = 0;

    for (const company of childCompanies) {
      setRefreshingCompanyId(company.company_id);

      // Update progress to show current company
      setRefreshProgress(prev => ({
        ...prev,
        steps: [{
          step: 'processing',
          status: 'in_progress',
          message: `Processing ${successful + failed + 1} of ${childCompanies.length}: ${company.company_name}...`
        }]
      }));

      try {
        const response = await api.post(`/companies/${company.company_id}/refresh-data`, refreshOptions);
        const result = response.data;
        result.company_name = company.company_name;
        results.push(result);
        if (result.success) {
          successful++;
        } else {
          failed++;
        }
      } catch (error) {
        results.push({
          company_id: company.company_id,
          company_name: company.company_name,
          success: false,
          error: error.response?.data?.detail || 'Failed to refresh data'
        });
        failed++;
      }
    }

    setRefreshProgress({
      type: 'all',
      steps: [{ step: 'completed', status: 'completed', message: `Processed ${childCompanies.length} companies under ${parentName}` }],
      success: failed === 0,
      error: null,
      total_companies: childCompanies.length,
      successful,
      failed,
      companies: results
    });

    setRefreshing(false);
    setRefreshingCompanyId(null);
  };

  // Refresh data for a single company
  const handleRefreshCompany = async (companyId, companyName) => {
    const selections = companySelections[companyId] || {
      refresh_accounts: false,
      refresh_profit_loss: false,
      refresh_cashflow: false
    };

    if (!hasSelection(companyId)) {
      alert('Please select at least one data type to refresh');
      return;
    }

    const selectedTypes = [];
    if (selections.refresh_accounts) selectedTypes.push('Accounts');
    if (selections.refresh_profit_loss) selectedTypes.push('Profit & Loss');
    if (selections.refresh_cashflow) selectedTypes.push('Cash Flow');

    if (!confirm(`Refresh QuickBooks data for ${companyName}?\n\nSelected: ${selectedTypes.join(', ')}`)) return;

    setRefreshingCompanyId(companyId);
    setRefreshing(true);
    setShowProgressModal(true);
    setRefreshProgress({
      type: 'single',
      company_name: companyName,
      steps: [{ step: 'starting', status: 'in_progress', message: 'Starting data refresh...' }],
      success: null,
      error: null,
      summary: null
    });

    try {
      const response = await api.post(`/companies/${companyId}/refresh-data`, selections);
      const result = response.data;

      setRefreshProgress({
        type: 'single',
        company_name: companyName,
        steps: result.steps,
        success: result.success,
        error: result.error,
        summary: result.summary
      });
    } catch (error) {
      setRefreshProgress({
        type: 'single',
        company_name: companyName,
        steps: [{ step: 'error', status: 'error', message: error.response?.data?.detail || 'Failed to refresh data' }],
        success: false,
        error: error.response?.data?.detail || 'Failed to refresh data',
        summary: null
      });
    } finally {
      setRefreshing(false);
      setRefreshingCompanyId(null);
    }
  };

  // Refresh data for all companies
  const handleRefreshAll = async () => {
    if (!hasGlobalSelection()) {
      alert('Please select at least one data type to refresh');
      return;
    }

    const selectedTypes = [];
    if (globalSelections.refresh_accounts) selectedTypes.push('Accounts');
    if (globalSelections.refresh_profit_loss) selectedTypes.push('Profit & Loss');
    if (globalSelections.refresh_cashflow) selectedTypes.push('Cash Flow');

    if (!confirm(`Refresh QuickBooks data for ALL companies?\n\nSelected: ${selectedTypes.join(', ')}\n\nThis will process ${companies.length} companies and may take a few minutes.`)) return;

    setRefreshing(true);
    setShowProgressModal(true);
    setRefreshProgress({
      type: 'all',
      steps: [{ step: 'starting', status: 'in_progress', message: `Processing ${companies.length} companies...` }],
      success: null,
      error: null,
      total_companies: companies.length,
      successful: 0,
      failed: 0,
      companies: []
    });

    try {
      const response = await api.post('/companies/refresh-all-data', globalSelections);
      const result = response.data;

      setRefreshProgress({
        type: 'all',
        steps: [{ step: 'completed', status: 'completed', message: `Processed ${result.total_companies} companies` }],
        success: result.success,
        error: result.error,
        total_companies: result.total_companies,
        successful: result.successful,
        failed: result.failed,
        companies: result.companies
      });
    } catch (error) {
      setRefreshProgress({
        type: 'all',
        steps: [{ step: 'error', status: 'error', message: error.response?.data?.detail || 'Failed to refresh data' }],
        success: false,
        error: error.response?.data?.detail || 'Failed to refresh data',
        total_companies: companies.length,
        successful: 0,
        failed: 0,
        companies: []
      });
    } finally {
      setRefreshing(false);
    }
  };

  const closeProgressModal = () => {
    setShowProgressModal(false);
    setRefreshProgress(null);
  };

  // Render step status icon
  const renderStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <span style={{ color: '#10B981' }}>&#10003;</span>;
      case 'error':
        return <span style={{ color: '#EF4444' }}>&#10007;</span>;
      case 'in_progress':
        return <span style={{ color: '#F59E0B' }}>&#8635;</span>;
      default:
        return <span style={{ color: '#9CA3AF' }}>&#8226;</span>;
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="company-management">
      <div className="management-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ margin: 0 }}>Companies</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Global checkboxes for Refresh All */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginRight: '10px', fontSize: '0.85rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={globalSelections.refresh_accounts}
                  onChange={() => toggleGlobalSelection('refresh_accounts')}
                  disabled={refreshing}
                />
                Accts
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={globalSelections.refresh_profit_loss}
                  onChange={() => toggleGlobalSelection('refresh_profit_loss')}
                  disabled={refreshing}
                />
                P&L
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={globalSelections.refresh_cashflow}
                  onChange={() => toggleGlobalSelection('refresh_cashflow')}
                  disabled={refreshing}
                />
                CF
              </label>
            </div>
            <button
              onClick={handleRefreshAll}
              className="btn-secondary"
              disabled={refreshing || companies.length === 0 || !hasGlobalSelection()}
              title="Refresh QuickBooks data for all companies"
            >
              {refreshing && !refreshingCompanyId ? 'Refreshing...' : 'Refresh All'}
            </button>
            <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary">
              {showCreateForm ? 'Cancel' : '+ Add Company'}
            </button>
          </div>
        </div>

        {/* Search bar - below header */}
        <div style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            position: 'relative',
            flex: '1',
            maxWidth: '400px'
          }}>
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              display: 'flex',
              alignItems: 'center'
            }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search by company name, ID, or parent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                fontSize: '0.9rem',
                background: '#f9fafb',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
                e.target.style.background = '#fff';
                e.target.style.boxShadow = '0 0 0 3px rgba(25, 118, 210, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e0e0e0';
                e.target.style.background = '#f9fafb';
                e.target.style.boxShadow = 'none';
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '18px'
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {searchTerm && (
            <span style={{ fontSize: '0.85rem', color: '#666' }}>
              {(() => {
                const { grouped, noParent } = getGroupedCompanies();
                const total = Object.values(grouped).reduce((sum, g) => sum + g.children.length, 0) + noParent.length;
                return `${total} result${total !== 1 ? 's' : ''} found`;
              })()}
            </span>
          )}
        </div>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreate} className="create-form">
          <h3>Add New Company</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>QuickBooks ID (Realm ID) <span style={{ fontWeight: 'normal', color: '#666' }}>(optional)</span></label>
              <input
                type="text"
                value={formData.quickbooks_id}
                onChange={(e) => setFormData({ ...formData, quickbooks_id: e.target.value })}
                placeholder="1234567890"
              />
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                placeholder="Acme Corp"
              />
            </div>
            <div className="form-group">
              <label>Parent Company (optional)</label>
              {!showNewParentInput ? (
                <>
                  <select
                    value={formData.parent_company_id}
                    onChange={(e) => setFormData({ ...formData, parent_company_id: e.target.value })}
                  >
                    <option value="">-- No Parent Company --</option>
                    {parentCompanies.map((pc) => (
                      <option key={pc.parent_company_id} value={pc.parent_company_id}>
                        {pc.parent_company_name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewParentInput(true);
                      setFormData({ ...formData, parent_company_id: '', new_parent_company_name: '' });
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      background: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    + Create New Parent Company
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={formData.new_parent_company_name}
                    onChange={(e) => setFormData({ ...formData, new_parent_company_name: e.target.value })}
                    placeholder="New Parent Company Name"
                    required={showNewParentInput}
                  />
                  <small style={{ color: '#10B981', display: 'block', marginTop: '4px' }}>
                    Creating new parent company (ID will be auto-generated)
                  </small>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewParentInput(false);
                      setFormData({ ...formData, new_parent_company_name: '' });
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      background: '#6B7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Select Existing Parent Company
                  </button>
                </>
              )}
            </div>
            <div className="form-group full-width">
              <label>QuickBooks Refresh Token <span style={{ fontWeight: 'normal', color: '#666' }}>(optional)</span></label>
              <textarea
                value={formData.refresh_token}
                onChange={(e) => setFormData({ ...formData, refresh_token: e.target.value })}
                placeholder="QuickBooks OAuth refresh token"
                rows={3}
              />
            </div>
          </div>
          <button type="submit" className="btn-primary">Add Company</button>
        </form>
      )}


      {/* Hierarchical Company List */}
      {(() => {
        const { grouped, noParent } = getGroupedCompanies();
        const parentGroups = Object.values(grouped);

        return (
          <div className="company-hierarchy">
            {/* Parent Company Groups */}
            {parentGroups.map((group) => (
              <div key={group.parent_company_id} className="parent-group" style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {/* Parent Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f5f5f5',
                    borderBottom: collapsedParents[group.parent_company_id] ? 'none' : '1px solid #e0e0e0',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleParentCollapse(group.parent_company_id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '1.2rem', color: '#666' }}>
                      {collapsedParents[group.parent_company_id] ? '▶' : '▼'}
                    </span>
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '1rem', color: '#333' }}>
                        {group.parent_company_name}
                      </span>
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '0.8rem',
                        color: '#666',
                        background: '#e0e0e0',
                        padding: '2px 8px',
                        borderRadius: '12px'
                      }}>
                        {group.children.length} {group.children.length === 1 ? 'company' : 'companies'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefreshParent(group.parent_company_id, group.parent_company_name);
                    }}
                    className="btn-small"
                    disabled={refreshing}
                    style={{
                      background: refreshing ? '#93c5fd' : '#1976d2',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: refreshing ? 'not-allowed' : 'pointer',
                      fontSize: '0.8rem'
                    }}
                    title="Refresh all data (Accounts, P&L, Cash Flow) for all companies under this parent"
                  >
                    Refresh All ({group.children.length})
                  </button>
                </div>

                {/* Child Companies */}
                {!collapsedParents[group.parent_company_id] && (
                  <table className="data-table" style={{ margin: 0, borderRadius: 0 }}>
                    <thead>
                      <tr>
                        <th>Company ID</th>
                        <th>Company Name</th>
                        <th style={{ textAlign: 'center', width: '60px' }}>Accts</th>
                        <th style={{ textAlign: 'center', width: '60px' }}>P&L</th>
                        <th style={{ textAlign: 'center', width: '60px' }}>CF</th>
                        <th style={{ width: '200px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.children.map((company) => (
                        <tr key={company.company_id}>
                          <td style={{ fontSize: '0.85rem', color: '#666' }}>{company.company_id}</td>
                          <td>{company.company_name}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={companySelections[company.company_id]?.refresh_accounts ?? false}
                              onChange={() => toggleCompanySelection(company.company_id, 'refresh_accounts')}
                              disabled={refreshing}
                              title="Refresh Accounts"
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={companySelections[company.company_id]?.refresh_profit_loss ?? false}
                              onChange={() => toggleCompanySelection(company.company_id, 'refresh_profit_loss')}
                              disabled={refreshing}
                              title="Refresh Profit & Loss"
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={companySelections[company.company_id]?.refresh_cashflow ?? false}
                              onChange={() => toggleCompanySelection(company.company_id, 'refresh_cashflow')}
                              disabled={refreshing}
                              title="Refresh Cash Flow"
                            />
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleRefreshCompany(company.company_id, company.company_name)}
                                className="btn-small btn-refresh"
                                disabled={refreshing || !hasSelection(company.company_id)}
                                title="Refresh selected data types"
                              >
                                {refreshingCompanyId === company.company_id ? 'Refreshing...' : 'Refresh'}
                              </button>
                              <button
                                onClick={() => handleEdit(company)}
                                className="btn-small"
                                style={{ background: '#1976d2', color: 'white', border: 'none' }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(company.company_id)}
                                className="btn-small btn-danger"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}

            {/* Companies without Parent */}
            {noParent.length > 0 && (
              <div className="parent-group" style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#fff3e0',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FolderIcon />
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '1rem', color: '#333' }}>
                        Standalone Companies (No Parent)
                      </span>
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '0.8rem',
                        color: '#666',
                        background: '#ffe0b2',
                        padding: '2px 8px',
                        borderRadius: '12px'
                      }}>
                        {noParent.length} {noParent.length === 1 ? 'company' : 'companies'}
                      </span>
                    </div>
                  </div>
                </div>

                <table className="data-table" style={{ margin: 0, borderRadius: 0 }}>
                  <thead>
                    <tr>
                      <th>Company ID</th>
                      <th>Company Name</th>
                      <th style={{ textAlign: 'center', width: '60px' }}>Accts</th>
                      <th style={{ textAlign: 'center', width: '60px' }}>P&L</th>
                      <th style={{ textAlign: 'center', width: '60px' }}>CF</th>
                      <th style={{ width: '200px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {noParent.map((company) => (
                      <tr key={company.company_id}>
                        <td style={{ fontSize: '0.85rem', color: '#666' }}>{company.company_id}</td>
                        <td>{company.company_name}</td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={companySelections[company.company_id]?.refresh_accounts ?? false}
                            onChange={() => toggleCompanySelection(company.company_id, 'refresh_accounts')}
                            disabled={refreshing}
                            title="Refresh Accounts"
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={companySelections[company.company_id]?.refresh_profit_loss ?? false}
                            onChange={() => toggleCompanySelection(company.company_id, 'refresh_profit_loss')}
                            disabled={refreshing}
                            title="Refresh Profit & Loss"
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={companySelections[company.company_id]?.refresh_cashflow ?? false}
                            onChange={() => toggleCompanySelection(company.company_id, 'refresh_cashflow')}
                            disabled={refreshing}
                            title="Refresh Cash Flow"
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleRefreshCompany(company.company_id, company.company_name)}
                              className="btn-small btn-refresh"
                              disabled={refreshing || !hasSelection(company.company_id)}
                              title="Refresh selected data types"
                            >
                              {refreshingCompanyId === company.company_id ? 'Refreshing...' : 'Refresh'}
                            </button>
                            <button
                              onClick={() => handleEdit(company)}
                              className="btn-small"
                              style={{ background: '#1976d2', color: 'white', border: 'none' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(company.company_id)}
                              className="btn-small btn-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* No results message */}
            {parentGroups.length === 0 && noParent.length === 0 && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                background: '#f5f5f5',
                borderRadius: '8px'
              }}>
                {searchTerm ? (
                  <p>No companies found matching "{searchTerm}"</p>
                ) : (
                  <p>No companies added yet. Click "+ Add Company" to get started.</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Edit Company Modal */}
      {showEditModal && editingCompany && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) handleCancelEdit();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#1976d2',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>
                Edit Company
              </h3>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: '1',
                  opacity: saving ? 0.5 : 1
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Company ID (read-only info) */}
              <div style={{
                background: '#f5f5f5',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px', textTransform: 'uppercase' }}>
                  QuickBooks ID (Realm)
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', color: '#333' }}>
                  {editingCompany.quickbooks_id}
                </div>
              </div>

              {/* Company Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#333'
                }}>
                  Company Name
                </label>
                <input
                  type="text"
                  value={editData.company_name}
                  onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  autoFocus
                />
              </div>

              {/* Parent Company */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#333'
                }}>
                  Parent Company
                </label>
                <select
                  value={editData.parent_company_id}
                  onChange={(e) => setEditData({ ...editData, parent_company_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white',
                    cursor: 'pointer',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                  <option value="">-- No Parent Company --</option>
                  {parentCompanies.map((pc) => (
                    <option key={pc.parent_company_id} value={pc.parent_company_id}>
                      {pc.parent_company_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Refresh Token */}
              <div style={{ marginBottom: '8px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#333'
                }}>
                  QuickBooks Refresh Token
                  <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                    (optional - leave empty to keep current)
                  </span>
                </label>
                <textarea
                  value={editData.refresh_token}
                  onChange={(e) => setEditData({ ...editData, refresh_token: e.target.value })}
                  placeholder="Paste new refresh token here..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              background: '#f9fafb',
              borderRadius: '0 0 12px 12px'
            }}>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d0d0d0',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#333',
                  fontSize: '0.95rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editData.company_name.trim()}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: saving || !editData.company_name.trim() ? '#93c5fd' : '#1976d2',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: saving || !editData.company_name.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {saving ? (
                  <>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && refreshProgress && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !refreshing) closeProgressModal();
        }}>
          <div className="modal-content" style={{ maxWidth: '700px' }}>
            <h3>
              {refreshProgress.type === 'single'
                ? `Refreshing Data: ${refreshProgress.company_name}`
                : 'Refreshing All Companies'
              }
            </h3>

            {/* Progress Steps */}
            <div className="progress-steps" style={{ marginTop: '1rem' }}>
              {refreshProgress.steps.map((step, index) => (
                <div
                  key={index}
                  className="progress-step"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    marginBottom: '8px',
                    backgroundColor: step.status === 'error' ? '#FEF2F2' :
                                    step.status === 'completed' ? '#F0FDF4' : '#FFFBEB',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                >
                  {renderStepIcon(step.status)}
                  <span>{step.message}</span>
                </div>
              ))}
            </div>

            {/* Summary for single company */}
            {refreshProgress.type === 'single' && refreshProgress.summary && refreshProgress.success && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#F0FDF4',
                borderRadius: '6px',
                border: '1px solid #BBF7D0'
              }}>
                <h4 style={{ marginBottom: '0.5rem', color: '#166534' }}>Data Loaded Successfully</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '0.9rem' }}>
                  {refreshProgress.summary.accounts !== null && (
                    <div>Accounts: <strong>{refreshProgress.summary.accounts}</strong></div>
                  )}
                  {refreshProgress.summary.profit_loss !== null && (
                    <div>Profit & Loss: <strong>{refreshProgress.summary.profit_loss}</strong></div>
                  )}
                  {refreshProgress.summary.cashflow !== null && (
                    <div>Cash Flow: <strong>{refreshProgress.summary.cashflow}</strong></div>
                  )}
                  {refreshProgress.summary.beginning_balances !== null && (
                    <div>Beginning Balances: <strong>{refreshProgress.summary.beginning_balances}</strong></div>
                  )}
                </div>
                {refreshProgress.summary.date_range && (
                  <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#166534' }}>
                    Date Range: {refreshProgress.summary.date_range.start} to {refreshProgress.summary.date_range.end}
                  </div>
                )}
              </div>
            )}

            {/* Summary for all companies */}
            {refreshProgress.type === 'all' && refreshProgress.total_companies > 0 && !refreshing && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{
                  padding: '1rem',
                  backgroundColor: refreshProgress.failed === 0 ? '#F0FDF4' : '#FEF3C7',
                  borderRadius: '6px',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ marginBottom: '0.5rem' }}>Summary</h4>
                  <div style={{ fontSize: '0.9rem' }}>
                    <div>Total Companies: <strong>{refreshProgress.total_companies}</strong></div>
                    <div style={{ color: '#166534' }}>Successful: <strong>{refreshProgress.successful}</strong></div>
                    {refreshProgress.failed > 0 && (
                      <div style={{ color: '#DC2626' }}>Failed: <strong>{refreshProgress.failed}</strong></div>
                    )}
                  </div>
                </div>

                {/* Individual company results */}
                {refreshProgress.companies && refreshProgress.companies.length > 0 && (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <h4 style={{ marginBottom: '0.5rem' }}>Company Results</h4>
                    {refreshProgress.companies.map((company, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          marginBottom: '6px',
                          backgroundColor: company.success ? '#F0FDF4' : '#FEF2F2',
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>
                            {renderStepIcon(company.success ? 'completed' : 'error')}
                            {' '}
                            <strong>{company.company_name || company.company_id}</strong>
                          </span>
                          {company.success && company.summary && (
                            <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>
                              {company.summary.accounts !== null && `${company.summary.accounts} accts`}
                              {company.summary.profit_loss !== null && `, ${company.summary.profit_loss} P&L`}
                              {company.summary.cashflow !== null && `, ${company.summary.cashflow} CF`}
                            </span>
                          )}
                        </div>
                        {company.error && (
                          <div style={{ color: '#DC2626', marginTop: '4px', fontSize: '0.8rem' }}>
                            {company.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Error message */}
            {refreshProgress.error && refreshProgress.type === 'single' && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#FEF2F2',
                borderRadius: '6px',
                border: '1px solid #FECACA',
                color: '#DC2626'
              }}>
                <strong>Error:</strong> {refreshProgress.error}
              </div>
            )}

            {/* Close button */}
            <div className="modal-actions">
              <button
                onClick={closeProgressModal}
                className={refreshing ? "btn-secondary" : "btn-primary"}
                disabled={refreshing}
              >
                {refreshing ? 'Processing...' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyManagement;
